import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import type { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import {
  buildContractFormSchema,
  type ContractFormValues,
} from "../schemas/contractFormSchema";

import { ContractInfoSection } from "./ContractInfoSection";
import { DiscountRateSection } from "./DiscountRateSection";
import { ModificationsSection } from "./ModificationsSection";
import { PaymentInfoSection } from "./PaymentInfoSection";

// Phase 6a.23 — lazy-load the FX section. The TMS 21 / IAS 21 sub-tree
// (FxSection + HistoricalRatesTable + PaymentDateRatesTable +
// ExcelPasteDialog + generatePaymentDates) accounts for ~25 KB of the
// contract form bundle; only ~25% of engagements need it (foreign
// currency leases). Splitting it out keeps the main bundle under the
// Phase 6a.23 600 KB ceiling while accepting a 50-200 ms first-render
// delay for the accordion section.
const ContractFxSection = React.lazy(() =>
  import("./ContractFxSection").then((m) => ({
    default: m.ContractFxSection,
  })),
);

// Phase 6a.20c — lazy-load the Termination section. Same split rationale
// as ContractFxSection — only a minority of contracts terminate inside
// the audit window, and the section's fields/dropdowns/conditional
// multi-currency block add weight to the form bundle that most
// engagements never pay for.
const TerminationSection = React.lazy(() =>
  import("./TerminationSection").then((m) => ({
    default: m.TerminationSection,
  })),
);

// Phase 6a.20d — lazy-load the Index Linkage section. The configuration
// block + useFieldArray-driven reset cards + soft-validation hints add
// another ~10 KB to the contract form; only contracts with TÜFE/ÜFE
// clauses ever exercise it, so the cost stays out of the main bundle.
const IndexLinkageSection = React.lazy(() =>
  import("./IndexLinkageSection").then((m) => ({
    default: m.IndexLinkageSection,
  })),
);

// Phase 6a.20e — lazy-load the Prior Year Reconciliation section. Same
// rationale as the other optional sections: only audit engagements with
// a prior IFRS 16 implementation use it. The section adds ~12 KB of
// form fields + soft-validation hints + a confirmation dialog.
const PriorReconciliationSection = React.lazy(() =>
  import("./PriorReconciliationSection").then((m) => ({
    default: m.PriorReconciliationSection,
  })),
);

// Additional Measurement Inputs (IFRS 16.27(c)-(d), 16.32) — lazy-loaded
// like the other optional sections. Only contracts with an RVG, a
// purchase option, or an ownership transfer ever exercise it, so the
// field group stays out of the main bundle for the common case.
const MeasurementOptionsSection = React.lazy(() =>
  import("./MeasurementOptionsSection").then((m) => ({
    default: m.MeasurementOptionsSection,
  })),
);

// Phase 6a.12/F-5 — the 3 always-open sections are no longer rendered
// inside accordion items; they live directly under `<form>` so each
// section component can own its own header + section chrome (Spec §11.15.1).
// The 7 collapsible sections retain their accordion behaviour.

const DEFAULT_VALUES: ContractFormValues = {
  contractId: "",
  description: "",
  // Phase 6c.2 (Spec §21.7.1) — assetClass has NO default: the Select
  // renders a placeholder and the form-level Zod refinement blocks
  // submit until the auditor explicitly chooses one of the 6 classes.
  assetClass: undefined as unknown as ContractFormValues["assetClass"],
  commencementDate: "",
  leaseTermMonths: undefined as unknown as number,
  currency: "TRY",
  payment: {
    amount: undefined as unknown as number,
    frequency: "MONTHLY",
    timing: "IN_ARREARS",
    firstPaymentDate: "",
  },
  discountRateAnnual: undefined as unknown as number,
  discountRateType: "IBR",
  // Phase 6a.20d — index linkage starts OFF. Other index fields stay
  // undefined until the IndexLinkageSection toggle flips on.
  indexLinked: false,
};

export interface ContractFormContainerProps {
  /**
   * Phase 6a.10 — optional initial values used to hydrate the form on
   * mount. When omitted (legacy single-contract path), the form falls
   * back to `DEFAULT_VALUES`.
   */
  initialValues?: Partial<ContractFormValues>;
  /**
   * Phase 6a.10 — optional change notifier wired by `ContractEntryPage`
   * so the auto-draft hook can debounce-save engagement slot updates.
   * Fired on every form mutation including invalid intermediate states;
   * the hook is responsible for debouncing.
   */
  onValuesChange?: (values: ContractFormValues, isDirty: boolean) => void;
  /**
   * Phase 6a.10 — optional callback that surfaces the inner
   * `useForm` return so the parent can `flushSave()` on navigation
   * (forces the auto-draft hook to commit any pending debounce).
   */
  onFormReady?: (form: UseFormReturn<ContractFormValues>) => void;
  /**
   * Phase 6a.23 — engagement-scoped functional currency. Threaded from
   * `ContractEntryPage` so the FX section can decide whether the
   * current `currency` makes this an FX contract. Defaults to `"TRY"`
   * for legacy single-contract callers that don't yet pass it.
   */
  functionalCurrency?: string;
  /**
   * BB4 / BI — engagement-scoped audit period end (ISO YYYY-MM-DD).
   * Passed down to the FX section so the rate-table bulk-generate
   * buttons can cap their output at the audit window. Optional — the
   * generators treat `undefined` as "no upper bound", preserving the
   * legacy behaviour for single-contract / standalone callers.
   */
  auditPeriodEnd?: string;
  /**
   * Phase 6a.20e — engagement-scoped audit period start (ISO YYYY-MM-DD).
   * Threaded to the PriorReconciliationSection so the section can
   * surface the "typical Y-1 close" helper (auditPeriodStart minus 1
   * day) and warn when priorReportingDate falls after the audit period
   * start. Optional — the section still works without it (hint omitted).
   */
  auditPeriodStart?: string;
  /**
   * SPEC-SHARE-B2 — when true, the entire form renders disabled (viewer
   * on a shared engagement). All inputs/selects/buttons inside the form
   * become non-interactive via a wrapping <fieldset disabled>. Defaults
   * to false (full editing).
   */
  readOnly?: boolean;
}

/**
 * Contract form container — Phase 6a.8 (introduced) + 6a.10 (engagement
 * hookups).
 *
 * Wires react-hook-form + Zod schema, renders the 8-section accordion: 3
 * always-open required sections + 5 disabled placeholder sections (filled
 * in 6a.14–6a.16). The form is self-contained; Phase 6a.10 added three
 * optional props (`initialValues`, `onValuesChange`, `onFormReady`) so
 * the engagement-aware `ContractEntryPage` can hydrate, observe, and
 * flush without changing the form's internal ownership of its `useForm`
 * instance.
 */
// SPEC-SHARE-B2 — accordion item values, used to open every optional
// section for a read-only viewer (disabled triggers can't be clicked).
const ALL_SECTIONS = [
  "multi-currency",
  "modifications",
  "termination",
  "measurement-options",
  "index-linkage",
  "prior-reconciliation",
];

export function ContractFormContainer({
  initialValues,
  onValuesChange,
  onFormReady,
  functionalCurrency = "TRY",
  auditPeriodEnd,
  auditPeriodStart,
  readOnly = false,
}: ContractFormContainerProps = {}) {
  const { t } = useTranslation();

  // Phase 6a.23 — schema is rebuilt whenever `functionalCurrency`
  // changes (in practice once-per-mount: the engagement's functional
  // currency is fixed by the time the contract form loads). useMemo
  // keeps the resolver stable across re-renders.
  const schema = React.useMemo(
    () => buildContractFormSchema({ functionalCurrency }),
    [functionalCurrency],
  );

  const form = useForm<ContractFormValues>({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { ...DEFAULT_VALUES, ...initialValues },
  });

  // Expose the form to the parent so it can flushSave() on navigation.
  React.useEffect(() => {
    onFormReady?.(form);
  }, [form, onFormReady]);

  // Notify parent of value changes (every keystroke). Debouncing/save
  // strategy is the parent's responsibility — typically the
  // `useEngagementContractDraft` hook.
  const watched = useWatch({ control: form.control });
  React.useEffect(() => {
    if (!onValuesChange) return;
    onValuesChange(form.getValues(), form.formState.isDirty);
    // `watched` is the dependency that triggers the effect on each
    // form mutation; `form` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watched, onValuesChange]);

  // ── Phase 6e.4.7 (P1) — FX accordion auto-open ──────────────────────
  // When the contract becomes multi-currency (currency ≠ functionalCurrency),
  // auto-reveal the FX configuration section. Closing the section is left
  // to the auditor; we only re-open on the NON-FX → FX transition, so an
  // explicit close stays closed for the rest of the editing session.
  // Toggling currency back to functional and then back to foreign re-arms
  // the open.
  const watchedCurrency = useWatch({ control: form.control, name: "currency" });
  const isFxContract = Boolean(
    watchedCurrency &&
      functionalCurrency &&
      watchedCurrency !== functionalCurrency,
  );

  const [accordionValue, setAccordionValue] = React.useState<string[]>(() =>
    readOnly ? ALL_SECTIONS : isFxContract ? ["multi-currency"] : [],
  );

  // Phase 6e.4.8 (Bug X1) — initialise to `false` so the FIRST time
  // `isFxContract` evaluates to `true` (which can happen on mount when
  // `functionalCurrency` prop is non-TRY and the contract currency
  // defaults to "TRY" — i.e. every non-TRY-functional engagement that
  // creates a fresh contract), the useEffect detects it as a transition
  // and opens the accordion.
  //
  // The previous `useRef(isFxContract)` captured the value on first
  // render; if RHF's `useWatch` resolved currency synchronously on that
  // render, `lastIsFxRef.current` was already `true` and the
  // transition check (`true && !true`) short-circuited. Net result:
  // the accordion stayed closed even though `isFxContract` was `true`
  // throughout. The `useState` lazy initialiser on line 167 still
  // handles the initial-open case correctly.
  const lastIsFxRef = React.useRef(false);
  React.useEffect(() => {
    if (isFxContract && !lastIsFxRef.current) {
      // Transition from non-FX → FX. Add "multi-currency" to the open
      // set without disturbing any other open items.
      setAccordionValue((prev) =>
        prev.includes("multi-currency") ? prev : [...prev, "multi-currency"],
      );
    }
    lastIsFxRef.current = isFxContract;
  }, [isFxContract]);

  return (
    <FormProvider {...form}>
      <form className="space-y-8">
        {/* SPEC-SHARE-B2 — a disabled fieldset natively makes every nested
            input/select/button non-interactive for a read-only viewer.
            `disabled:opacity-100` keeps the read view legible (no dimming);
            `min-w-0` preserves the grid/flex children. */}
        <fieldset
          disabled={readOnly}
          className="space-y-8 border-0 m-0 p-0 min-w-0 disabled:opacity-100"
          data-testid="contract-form-fieldset"
        >
          {/* ── 3 ALWAYS-OPEN SECTIONS (Phase 6a.12/F-5) ─────────────────
              Rendered directly; each section owns its bordered header. */}
          <ContractInfoSection />
        <PaymentInfoSection auditPeriodEnd={auditPeriodEnd} />
        <DiscountRateSection />

        <Accordion
          type="multiple"
          // Phase 6e.4.7 — controlled state so the FX section can be
          // auto-opened when the contract becomes multi-currency.
          // Termination is live as of Phase 6a.20c; index linkage is
          // live as of Phase 6a.20d; prior reconciliation is live as
          // of Phase 6a.20e. All five accordion items are now active.
          value={accordionValue}
          onValueChange={setAccordionValue}
          className="w-full"
        >
          {/* Phase 6a.23 — Çoklu Para Birimi (FX) section is now live.
              Wrapped in Suspense because the section is a lazy chunk
              (see import). The fallback is null — the AccordionItem
              chrome (trigger + content shell) renders synchronously
              even while the inner FX content streams in. */}
          <React.Suspense fallback={null}>
            <ContractFxSection
              functionalCurrency={functionalCurrency}
              auditPeriodEnd={auditPeriodEnd}
            />
          </React.Suspense>

          {/* Phase 6a.22 — Modifikasyonlar section is now live. */}
          <ModificationsSection />

          {/* Phase 6a.20c — Sonlandırma section is now live (IFRS 16 §46(a)). */}
          <AccordionItem value="termination">
            <AccordionTrigger
              className="text-base font-medium"
              data-testid="termination-section-trigger"
            >
              {t("form.section.termination")}
            </AccordionTrigger>
            <AccordionContent>
              <React.Suspense
                fallback={
                  <p className="text-muted-foreground text-sm py-4">…</p>
                }
              >
                <TerminationSection
                  functionalCurrency={functionalCurrency}
                  auditPeriodEnd={auditPeriodEnd}
                />
              </React.Suspense>
            </AccordionContent>
          </AccordionItem>

          {/* Additional Measurement Inputs (IFRS 16.27(c)-(d), 16.32). */}
          <AccordionItem value="measurement-options">
            <AccordionTrigger
              className="text-base font-medium"
              data-testid="measurement-options-section-trigger"
            >
              {t("form.section.measurementOptions")}
            </AccordionTrigger>
            <AccordionContent>
              <React.Suspense
                fallback={
                  <p className="text-muted-foreground text-sm py-4">…</p>
                }
              >
                <MeasurementOptionsSection
                  functionalCurrency={functionalCurrency}
                />
              </React.Suspense>
            </AccordionContent>
          </AccordionItem>

          {/* Phase 6a.20d — Endeks Bağlama section is now live (IFRS 16 §42(b)). */}
          <AccordionItem value="index-linkage">
            <AccordionTrigger
              className="text-base font-medium"
              data-testid="index-linkage-section-trigger"
            >
              {t("form.section.indexLinkage")}
            </AccordionTrigger>
            <AccordionContent>
              <React.Suspense
                fallback={
                  <p className="text-muted-foreground text-sm py-4">…</p>
                }
              >
                <IndexLinkageSection
                  functionalCurrency={functionalCurrency}
                />
              </React.Suspense>
            </AccordionContent>
          </AccordionItem>

          {/* Phase 6a.20e — Önceki Dönem Mutabakatı section is now live
              (IFRS 16 §3.8/3.9 audit deliverable). */}
          <AccordionItem value="prior-reconciliation">
            <AccordionTrigger
              className="text-base font-medium"
              data-testid="prior-reconciliation-section-trigger"
            >
              {t("form.section.priorReconciliation")}
            </AccordionTrigger>
            <AccordionContent>
              <React.Suspense
                fallback={
                  <p className="text-muted-foreground text-sm py-4">…</p>
                }
              >
                <PriorReconciliationSection
                  functionalCurrency={functionalCurrency}
                  auditPeriodStart={auditPeriodStart}
                />
              </React.Suspense>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        </fieldset>
      </form>
    </FormProvider>
  );
}
